import styles from '../../../styles/modules/ChildProfile.module.css';
import defaultProfileImg from '../../../assets/images/profile-picture.png';
import { AiTwotoneDelete } from "react-icons/ai";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { FaCircleCheck } from 'react-icons/fa6';
import { IoCloseCircle, IoPersonAdd } from 'react-icons/io5';
import { useEffect, useState } from 'react';
import * as Yup from 'yup'
import { EventAPI } from '../../../api/EventAPI';
import { CoachAPI } from '../../../api/CoachAPI';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { rootAction } from '../../../redux/reducer/rootReducer';

// ✅ Updated validation with phone number validation
const validationObj = Yup.object({
    name: Yup.string().required("Full name required"),
    email: Yup.string().email("Enter valid email").required("Email is required"),
    relation: Yup.string().required("Relation is required"),
    phone: Yup.string()
        .required("Phone is required")
        .matches(/^[0-9+\s]+$/, "Phone must contain only digits and +")
        .min(10, "Phone must be at least 10 digits")
        .max(15, "Phone must be at most 15 digits")
        .test('valid-phone', 'Please enter a valid phone number', function(value) {
            if (!value) return true;
            // Remove all non-digit characters except +
            const cleaned = value.replace(/[^\d+]/g, '');
            // Count only digits (not +)
            const digitCount = cleaned.replace(/[^0-9]/g, '').length;
            // Allow 10-15 digits (US/International)
            return digitCount >= 10 && digitCount <= 15;
        }),
})

const ChildCuardianTable = ({ childId, userInfo, startLoading, finishLoading }) => {
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const [fieldErrors, setFieldErrors] = useState({});
    const [memberData, setMemberData] = useState({});
    const [isAdd, setIsAdd] = useState(false);
    const [guardians, setGuardians] = useState([]);
    const [isUpdate, setIsUpdate] = useState(false);

    const dispatch = useDispatch();

    const handleOnChange = (event) => {
        const { name, value } = event.target;
        // Clean phone number input (only digits and +)
        if (name === 'phone') {
            const cleaned = value.replace(/[^\d+]/g, '');
            setMemberData({ ...memberData, [name]: cleaned });
        } else {
            setMemberData({ ...memberData, [name]: value });
        }
        Object.keys(fieldErrors).length != 0 && setFieldErrors({});
    }

    const findAllGuardianByChild = async () => {
        try {
            const response = await CoachAPI.findAllGuardian(childId);
            setGuardians(response.Data)
        } catch (error) {
            console.log(error);
        }
    };
    useEffect(() => {
        if (childId) {
            findAllGuardianByChild();
        }
        isUpdate && setIsUpdate(false);
    }, [isUpdate]);

    const handleRemoveFamilyMember = async (guardianId) => {
        try {
            if(userInfo.userId===guardianId){
                dispatch(rootAction.setWarningMessage({ id: Date.now(), type: 'warning', content: "You can't remove yourself" }));
                return;
            }
            startLoading("loading3")
            await EventAPI.putRemoveFamilyMemberFromPlayer({
                guardianId: guardianId,
                playerId: childId,
                isChild: true
            })
            setIsUpdate(true);
        } catch (error) {
            console.log(error);
        } finally {
            finishLoading("loading3")
        }
    };

    const handleAddFamilyMemberToPlayer = async () => {
        try {
            await validationObj.validate(memberData, { abortEarly: false });
            const name = String(memberData.name || "").trim().split(/\s+/);
            if (!name[1]) {
                setFieldErrors({ name: "Enter first and last name" });
                return;
            }
            startLoading("loading3")
            const reqObj = {
                firstName: name[0] ?? '',
                lastName: name[1] ?? '',
                email: memberData.email,
                mobile: memberData.phone.startsWith('+') ? memberData.phone : "+" + memberData.phone,
                playerId: childId,
                relationType: memberData.relation,
                isChild: true,
                clubId: selectedClub.clubId
            }

            await EventAPI.postAddFamilyMemberToPlayer(reqObj);
            setIsAdd(false);
            setIsUpdate(true);
            setMemberData({});
        } catch (error) {
            console.log(error);
            if (error.inner) {
                let obj = {};
                error.inner.forEach(err => {
                    obj[err.path] = err.message
                })
                setFieldErrors(obj)
            }
        } finally {
            finishLoading("loading3")
        }
    };

    return (
        <div className={styles['child_guardian_container']}>
            <div className={styles['add_memeber_btn']}>
                <button onClick={() => setIsAdd(true)} type='button'> <IoPersonAdd className={styles['add_memeber_btn_icon']} />Add member</button>
            </div>
            <table className={styles['child_guardian_table']}>
                <thead>
                    <tr>
                        <th align='left'></th>
                        <th align='left'>Name</th>
                        <th align='left'>Email</th>
                        <th align='left'>Phone</th>
                        <th align='left'>Status</th>
                        <th align='left'>Relation</th>
                        <th align='center'>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {guardians?.map((guardian) =>
                        <tr key={guardian.guardianId}>
                            <td className={styles['guardian_img']} align='center'>
                                <img src={guardian?.profileImage ? import.meta.env.VITE_APP_BASE_URL + '/user/user-profile-img/' + guardian?.profileImage : defaultProfileImg} alt="" />
                            </td>
                            <td align='left'>{(guardian?.firstName ?? '') + " " + (guardian?.lastName ?? '')}</td>
                            <td align='left'>{guardian?.email}</td>
                            <td align='left'>{guardian?.phone}</td>
                            {guardian?.status === 'ACTIVE' &&
                                <td align='left' className={styles['guardian_status']} >
                                    <span className={styles['guardian_status_active']}></span> Active
                                </td>}
                            {guardian?.status === 'PENDING' &&
                                <td align='left' className={styles['guardian_status']} >
                                    <span className={styles['guardian_status_pending']}></span> Pending
                                </td>}
                            <td align='left' style={{ textTransform: 'capitalize' }}>{guardian?.relationshipType}</td>
                            <td align='center'> 
                                <AiTwotoneDelete 
                                    onClick={() => handleRemoveFamilyMember(guardian?.guardianId)} 
                                    style={{ 
                                        fontSize: '21px', 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        color: '#666'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.color = '#c62828';
                                        e.target.style.transform = 'scale(1.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.color = '#666';
                                        e.target.style.transform = 'scale(1)';
                                    }}
                                />
                            </td>
                        </tr>
                    )}
                    {isAdd &&
                        <tr>
                            <td colSpan={10}>
                                <div className={styles['add_memeber_to_player_form']}>
                                    <div style={{ flex: 1, minWidth: '130px' }}>
                                        <TextField
                                            error={!!fieldErrors['name']}
                                            helperText={fieldErrors['name'] || ''}
                                            name='name'
                                            label="Full Name"
                                            size='small'
                                            value={memberData?.name || ''}
                                            fullWidth
                                            sx={{ background: 'white' }}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div style={{ flex: 1, minWidth: '130px' }}>
                                        <TextField
                                            error={!!fieldErrors['email']}
                                            helperText={fieldErrors['email'] || ''}
                                            name='email'
                                            label="Email"
                                            size='small'
                                            value={memberData?.email || ''}
                                            fullWidth
                                            sx={{ background: 'white' }}
                                            onChange={handleOnChange}
                                        />
                                    </div>

                                    <div style={{ flex: 0.8, minWidth: '120px' }}>
                                        <TextField
                                            error={!!fieldErrors['phone']}
                                            helperText={fieldErrors['phone'] || ''}
                                            name='phone'
                                            label="Phone"
                                            size='small'
                                            value={memberData?.phone || ''}
                                            fullWidth
                                            sx={{ background: 'white' }}
                                            onChange={handleOnChange}
                                            placeholder="+1234567890"
                                        />
                                    </div>

                                    <div style={{ flex: '0 0 110px', minWidth: '100px' }}>
                                        <FormControl
                                            error={!!fieldErrors['relation']}
                                            sx={{ background: 'white', width: '100%' }}
                                            size="small"
                                        >
                                            <InputLabel sx={{ fontSize: '13px' }}>Relation</InputLabel>
                                            <Select
                                                onChange={handleOnChange}
                                                value={memberData.relation || ''}
                                                fullWidth
                                                label="Relation"
                                                name='relation'
                                                size='small'
                                                error={!!fieldErrors['relation']}
                                                sx={{ height: '36px', fontSize: '13px' }}
                                            >
                                                <MenuItem value="father">Father</MenuItem>
                                                <MenuItem value="mother">Mother</MenuItem>
                                                <MenuItem value="grand_father">Grand Father</MenuItem>
                                                <MenuItem value="grand_mother">Grand Mother</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        paddingLeft: '4px'
                                    }}>
                                        <FaCircleCheck 
                                            onClick={handleAddFamilyMemberToPlayer} 
                                            style={{
                                                fontSize: '26px',
                                                color: '#2e7d32',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'scale(1.15)';
                                                e.target.style.color = '#1b5e20';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'scale(1)';
                                                e.target.style.color = '#2e7d32';
                                            }}
                                        />
                                        <IoCloseCircle 
                                            style={{
                                                fontSize: '26px',
                                                color: '#c62828',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.transform = 'scale(1.15)';
                                                e.target.style.color = '#b71c1c';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.transform = 'scale(1)';
                                                e.target.style.color = '#c62828';
                                            }}
                                            onClick={() => {
                                                setIsAdd(false);
                                                setMemberData({});
                                                Object.keys(fieldErrors).length != 0 && setFieldErrors({});
                                            }} 
                                        />
                                    </div>
                                </div>
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    )
}

export default ChildCuardianTable